/**
 * The matchmaking queue overlay — the FACEIT-style search flow, client half.
 *
 * Mounted by `play-route.ts` when the ONLINE mode's PLAY is pressed while the meta socket is
 * connected; everything here is presentation and intent. The queue itself — who is in it, when
 * it is full, who accepted — lives on the meta tier and arrives only through `mm:state`. This
 * module never decides an outcome: it renders the server's picture, emits `mm:queue` /
 * `mm:accept` / `mm:cancel`, and hands the final `mm:start` payload back to the route. Same
 * doctrine as the arena's pickups: intent goes up, truth comes down.
 *
 * Socket contract (the backend gateway implements exactly this):
 *   emit  mm:queue {} · mm:cancel {} · mm:accept {}
 *   on    mm:state   { phase, teamSize, teams, secondsInQueue, acceptDeadline? }
 *         mm:start   { matchId, room, ticket, expiresAt }
 *         mm:aborted { reason }
 *
 * Lifecycle: `mountQueueOverlay` appends a fixed full-screen layer to `document.body` (the
 * same pattern as `ui.ts`'s `openModal`) and returns a disposer. The disposer is idempotent,
 * detaches every socket listener, clears every timer, and emits `mm:cancel` unless the match
 * already started — so the route's normal teardown (navigating away) is also "leave queue".
 *
 * z-index 16: above the play route's own edge controls (15), below the shell header (20) — a
 * player can still click the nav while queued, and that navigation IS the cancel.
 *
 * The 5s fallback matters more than it looks: `make play` runs with no backend at all, and a
 * connected socket whose gateway lacks the mm:* handlers would otherwise strand the player on
 * a spinner forever. No `mm:state` within FALLBACK_MS of `mm:queue` → `onFallback()`, and the
 * route runs today's direct launch instead. The queue is an enhancement, never a gate.
 */
import { t } from '../../i18n';
import { button, card, icon, plateContent } from '../ui';
import { playSample } from '../menu-sfx';

export interface QueueSlot {
  username: string;
  level: number;
  accepted: boolean;
}

export type QueuePhase = 'searching' | 'found' | 'starting';

export interface QueueStateMessage {
  phase: QueuePhase;
  teamSize: number;
  /** Two teams, each exactly `teamSize` entries; null = an unfilled slot. */
  teams: [Array<QueueSlot | null>, Array<QueueSlot | null>];
  secondsInQueue: number;
  /** Epoch ms — present only while `phase === 'found'`. */
  acceptDeadline?: number;
  /**
   * Seconds until the lane stops waiting for people and fills the empty seats with bots
   * (2026-09-11). Present only while searching and only when the backend has backfill on.
   */
  botFillInSeconds?: number;
}

export interface QueueStartMessage {
  matchId: string;
  room: string;
  ticket: string;
  expiresAt: string;
}

export type QueueAbortReason = 'declined' | 'timeout' | 'cancelled' | 'error' | 'cooldown';

/** The slice of Socket.io this module touches — a seam for the tests, satisfied structurally
 * by the real `socket` singleton. The handler params are `any[]` on purpose: Socket.io's own
 * `on`/`off` signatures use a listener type that `never[]`/`unknown[]` params refuse to
 * absorb, and this interface exists to accept the real client, not to re-type it. */
export interface QueueSocketLike {
  connected: boolean;
  emit(event: string, payload?: unknown): unknown;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  on(event: string, handler: (...args: any[]) => void): unknown;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  off(event: string, handler: (...args: any[]) => void): unknown;
}

export interface QueueOverlayOptions {
  socket: QueueSocketLike;
  /** The local player's username, so their own slot reads as theirs. */
  username: string;
  /**
   * Which queue lane to wait in — a mode key from `shared/src/game-modes.ts`.
   *
   * A choice of QUEUE, not of rules: the room the group lands in is pinned server-side and
   * verifies its ticket against its own mode, so an unknown value here gets the default
   * lane rather than a room running rules nobody asked for.
   */
  mode: string;
  onStart(message: QueueStartMessage): void;
  onAborted(reason: QueueAbortReason): void;
  /** The backend never answered `mm:queue` — fall back to the direct launch. */
  onFallback(): void;
}

export interface QueueOverlayHandle {
  dispose(): void;
}

/** How long a silent backend gets before the overlay gives up and falls back. */
export const QUEUE_FALLBACK_MS = 5000;

function formatElapsed(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.max(0, Math.floor(totalSeconds % 60));
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

/** One read of the OS setting, per call rather than cached — a person can change it mid-session,
 * same reasoning as `fx.ts`'s copy of this — this module has no dependency on `fx.ts` otherwise
 * and a one-line check is not worth importing it for. */
function reducedMotion(): boolean {
  return typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;
}

/**
 * The one-shot "match found" beat: a scale-and-glow pulse on the status panel, played exactly
 * once on the searching→found transition. Not a loop — the loop is the sweep, and this is the
 * moment the sweep was building toward.
 */
function foundBeat(el: HTMLElement): void {
  playSample('matchFound');
  if (reducedMotion() || typeof el.animate !== 'function') return;
  el.animate(
    [
      { transform: 'scale(1)', filter: 'drop-shadow(0 0 0 rgba(23,224,201,0))' },
      { transform: 'scale(1.045)', filter: 'drop-shadow(0 0 22px rgba(23,224,201,0.65))', offset: 0.45 },
      { transform: 'scale(1)', filter: 'drop-shadow(0 0 0 rgba(23,224,201,0))' },
    ],
    { duration: 480, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' },
  );
}

export function mountQueueOverlay(opts: QueueOverlayOptions): QueueOverlayHandle {
  // ── Static scaffold — built once; only the dynamic islands re-render per mm:state. ────────
  const root = document.createElement('div');
  root.dataset.slot = 'queue-overlay';
  root.className =
    'fixed inset-0 z-[16] flex flex-col items-center justify-center gap-5 bg-black/65 px-4 ' +
    'pb-[calc(76px+env(safe-area-inset-bottom))] md:pb-8 overflow-y-auto';

  // The status window: a `kit-header` carrying the title and the elapsed clock in a `kit-chip`,
  // a `kit-bar` whose fill sweeps while searching (the one animation this overlay lets run
  // forever), and the fill count — one painted window, and the element the "match found" beat
  // (below) plays on.
  const title = document.createElement('h2');
  title.dataset.queue = 'title';
  title.className = 'kit-title font-heading uppercase tracking-[0.08em] text-[16px] sm:text-[18px] truncate';
  title.textContent = t('play.queue.searching');

  const elapsedChip = document.createElement('span');
  elapsedChip.className = 'kit-chip [--kh:30px] shrink-0 font-heading text-[14px] tabular-nums';
  const elapsed = document.createElement('span');
  elapsed.dataset.queue = 'elapsed';
  elapsed.className = 'kit-label';
  elapsed.textContent = '00:00';
  elapsedChip.append(icon('time', { size: 26, className: '-ml-3' }), elapsed);

  const heading = document.createElement('div');
  heading.className = 'kit-header [--kh:50px] justify-between gap-3 -mx-1';
  heading.append(title, elapsedChip);

  // The search bar. Visibility lives on the wrapper (`kit-bar` sets its own `display`). Reduced
  // motion gets a half-full static fill — it still reads as "in progress" without moving.
  const sweep = document.createElement('div');
  sweep.dataset.queue = 'sweep';
  sweep.setAttribute('aria-hidden', 'true');
  const sweepBar = document.createElement('div');
  sweepBar.className = 'kit-bar [--kh:24px] w-full';
  const sweepFill = document.createElement('div');
  sweepFill.className = 'kit-bar-fill';
  sweepFill.style.width = reducedMotion() ? '50%' : '34%';
  sweepBar.append(sweepFill);
  sweep.append(sweepBar);
  if (!reducedMotion() && typeof sweepFill.animate === 'function') {
    // Left to right and back: the lit tip travels the groove for as long as the search runs.
    sweepFill.animate(
      [{ transform: 'translateX(0)' }, { transform: 'translateX(194%)' }],
      { duration: 1400, direction: 'alternate', iterations: Infinity, easing: 'ease-in-out' },
    );
  }

  const fill = document.createElement('p');
  fill.dataset.queue = 'fill';
  fill.className = 'text-center text-[13px] text-text-dim font-heading uppercase tracking-wide';
  fill.textContent = '';

  // No backfill countdown on screen (2026-09-11: players do not need to know). The wire
  // still carries `botFillInSeconds`; the overlay simply does not draw it — the wait ends when
  // it ends, and the accept screen is where a player learns who is in the match.
  const statusPanel = card([heading, sweep, fill], { className: 'w-full max-w-[420px] kit-pop' });

  // The two team columns, in a painted window of their own. Stacked under 640px — two
  // five-row columns side by side do not fit a 320px viewport at legible size; one column of
  // ten does. The window stays hidden until the first `mm:state` gives it rows.
  const gridWindow = document.createElement('div');
  gridWindow.className = 'hidden kit-window [--kw-pad:16px] [--kw-rim:20px] w-full max-w-[640px]';
  const grid = document.createElement('div');
  grid.dataset.queue = 'teams';
  grid.className = 'grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-5 w-full';
  gridWindow.append(grid);

  // Accept countdown bar + the action row. Hidden until `found`; the wrapper carries `hidden`.
  const countdown = document.createElement('div');
  countdown.dataset.queue = 'countdown';
  countdown.className = 'hidden w-full max-w-[420px]';
  const countdownBar = document.createElement('div');
  countdownBar.className = 'kit-bar [--kh:24px] w-full';
  const countdownFill = document.createElement('div');
  countdownFill.className = 'kit-bar-fill';
  countdownFill.dataset.tone = 'red';
  countdownFill.style.width = '100%';
  countdownBar.append(countdownFill);
  countdown.append(countdownBar);

  const actions = document.createElement('div');
  actions.className = 'flex flex-col items-stretch gap-2 w-full max-w-[420px]';

  // Visibility lives on a wrapper div, never on the button itself: `hidden` and the plate's
  // `inline-flex` both set `display`, and Tailwind resolves that collision by stylesheet order,
  // not class order — the same trap play-route.ts documents for `fixed`/`relative`.
  const acceptWrap = document.createElement('div');
  acceptWrap.dataset.queue = 'accept-wrap';
  acceptWrap.className = 'hidden';
  const accept = button(t('play.queue.accept'), { variant: 'primary', size: 'xl', className: 'w-full' });
  accept.dataset.queue = 'accept';
  acceptWrap.append(accept);

  const cancel = button(t('play.queue.cancel'), { variant: 'secondary', className: 'w-full' });
  cancel.dataset.queue = 'cancel';

  actions.append(acceptWrap, cancel);
  root.append(statusPanel, gridWindow, countdown, actions);
  document.body.append(root);

  // ── State ──────────────────────────────────────────────────────────────────────────────────
  let phase: QueuePhase = 'searching';
  let disposed = false;
  let started = false;
  let sawState = false;
  let accepted = false;
  /** Server-reported seconds, ticked locally between states so the clock never stalls. */
  let queueSeconds = 0;
  let acceptDeadline: number | null = null;
  /** The full accept window, captured at the first `found` state — the bar's denominator. */
  let acceptWindowMs: number | null = null;

  const fallbackTimer = setTimeout(() => {
    if (disposed || sawState) return;
    // Silence: the gateway is absent — OR it queued us and the state message was merely
    // lost/late. Emitting mm:cancel covers both: the server documents cancel as a no-op
    // when not queued, and skipping it left a live queue entry behind a direct launch,
    // which times out a future group's ready check (review I5).
    teardown(true);
    opts.onFallback();
  }, QUEUE_FALLBACK_MS);

  const elapsedTicker = setInterval(() => {
    if (phase !== 'searching') return;
    queueSeconds += 1;
    elapsed.textContent = formatElapsed(queueSeconds);
  }, 1000);

  const countdownTicker = setInterval(() => {
    if (acceptDeadline === null || acceptWindowMs === null || acceptWindowMs <= 0) return;
    const remaining = Math.max(0, acceptDeadline - Date.now());
    countdownFill.style.width = `${(remaining / acceptWindowMs) * 100}%`;
  }, 100);

  function slotRow(slot: QueueSlot | null): HTMLElement {
    const row = document.createElement('div');
    if (slot === null) {
      // An empty seat is a recess in the metal — no loop here, the sweep above is the one
      // animation this overlay lets run forever.
      row.className = 'kit-well min-h-[44px]';
      row.dataset.queueSlot = 'empty';
      return row;
    }
    // A backfilled seat arrives from the server as a name and a level like anyone else's
    // (2026-09-11): nothing here tells a bot from a person, by design — the player is not
    // told how many of the other nine are the server's.
    const mine = slot.username === opts.username;
    row.dataset.queueSlot = slot.accepted ? 'accepted' : 'filled';
    // A steel plate; the player's own lit teal, so "me" never relies on a hairline.
    row.className = `kit-plate [--kp-rim:10px] [--kp-pad:6px_12px] min-h-[44px] flex items-center gap-2 ${mine ? 'kit-selected' : ''}`;
    const name = document.createElement('span');
    name.className = 'kit-label font-heading uppercase tracking-wide text-sm truncate';
    name.textContent = slot.username;
    const level = document.createElement('span');
    level.className = 'ml-auto shrink-0 inline-flex items-center gap-1 text-[11px] font-heading text-gold';
    level.append(icon('star', { size: 16 }));
    const levelText = document.createElement('span');
    levelText.textContent = t('play.queue.levelShort', { level: slot.level });
    level.append(levelText);
    row.append(name, level);
    if (slot.accepted) row.append(icon('done', { size: 24, className: 'kit-pop' }));
    return row;
  }

  function render(state: QueueStateMessage): void {
    // The server owns this shape, but a malformed message must not throw inside a socket
    // listener and wedge the overlay — ignore it and keep the last good render.
    if (!Array.isArray(state?.teams) || typeof state.teamSize !== 'number') return;
    gridWindow.classList.remove('hidden');
    grid.replaceChildren();
    for (const team of state.teams) {
      const column = document.createElement('div');
      column.className = 'flex flex-col gap-1.5';
      for (let i = 0; i < state.teamSize; i++) column.append(slotRow(team[i] ?? null));
      grid.append(column);
    }
    const filled = state.teams.flat().filter(Boolean).length;
    fill.textContent = t('play.queue.slotsFilled', { filled, total: state.teamSize * 2 });
    elapsed.textContent = formatElapsed(state.secondsInQueue);

    if (state.phase === 'found') {
      title.textContent = t('play.queue.found');
      sweep.classList.add('hidden');
      acceptWrap.classList.remove('hidden');
      countdown.classList.remove('hidden');
      plateContent(cancel).textContent = t('play.queue.decline');
      if (state.acceptDeadline !== undefined && acceptWindowMs === null) {
        acceptDeadline = state.acceptDeadline;
        acceptWindowMs = Math.max(1, state.acceptDeadline - Date.now());
      }
    } else if (state.phase === 'starting') {
      title.textContent = t('play.queue.starting');
      acceptWrap.classList.add('hidden');
      countdown.classList.add('hidden');
    } else {
      // Back to searching (someone declined and the group refills): reset the found chrome.
      title.textContent = t('play.queue.searching');
      sweep.classList.remove('hidden');
      acceptWrap.classList.add('hidden');
      countdown.classList.add('hidden');
      plateContent(cancel).textContent = t('play.queue.cancel');
      accepted = false;
      accept.disabled = false;
      setAcceptLabel(t('play.queue.accept'));
      acceptDeadline = null;
      acceptWindowMs = null;
    }
  }

  // ── Socket wiring ─────────────────────────────────────────────────────────────────────────
  const onState = (state: QueueStateMessage): void => {
    if (disposed) return;
    sawState = true;
    const prevPhase = phase;
    phase = state.phase;
    queueSeconds = state.secondsInQueue;
    render(state);
    // The one-shot beat, on the transition INTO found — never on a later `mm:state` that
    // merely repeats it (someone else's accept), and never on a rewind back into searching.
    if (prevPhase !== 'found' && state.phase === 'found') foundBeat(statusPanel);
  };
  const onStart = (message: QueueStartMessage): void => {
    if (disposed) return;
    started = true;
    teardown(false);
    opts.onStart(message);
  };
  const onAborted = (payload: { reason?: QueueAbortReason } | undefined): void => {
    if (disposed) return;
    // The server already ended this queue entry — cancelling it again would be noise.
    teardown(false);
    opts.onAborted(payload?.reason ?? 'error');
  };

  opts.socket.on('mm:state', onState);
  opts.socket.on('mm:start', onStart);
  opts.socket.on('mm:aborted', onAborted);
  opts.socket.emit('mm:queue', { mode: opts.mode });

  /** Into the plate's own label node: the face paints over any other child. */
  function setAcceptLabel(text: string): void {
    plateContent(accept).textContent = text;
  }

  accept.addEventListener('click', () => {
    playSample('start');
    if (accepted) return;
    accepted = true;
    accept.disabled = true;
    // Optimistic label only — the slot's green edge waits for the server's mm:state.
    setAcceptLabel(t('play.queue.accepted'));
    opts.socket.emit('mm:accept', {});
  });

  cancel.addEventListener('click', () => {
    teardown(true);
    opts.onAborted('cancelled');
  });

  function teardown(emitCancel: boolean): void {
    if (disposed) return;
    disposed = true;
    clearTimeout(fallbackTimer);
    clearInterval(elapsedTicker);
    clearInterval(countdownTicker);
    opts.socket.off('mm:state', onState);
    opts.socket.off('mm:start', onStart);
    opts.socket.off('mm:aborted', onAborted);
    if (emitCancel && !started) opts.socket.emit('mm:cancel', {});
    root.remove();
  }

  return {
    // Route teardown: navigating away IS leaving the queue.
    dispose: () => teardown(true),
  };
}
